import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";

import { Grid } from "@mui/material";

import findSubcategoryObj from "@aclymatepackages/subcategories";

import EmployeesInput from "./EmployeesInput";
import OfficeInput from "./OfficeInput";
import VendorsInput from "./VendorsInput";
import EventsInput from "./EventsInput";

import { fetchOurApi } from "../../../helpers/utils/apiCalls";

const TransactionsTagsInputs = ({ transaction, editTransaction }) => {
  const {
    subcategory: transactionSubcategory,
    employees,
    office,
    event,
    vendorId,
    vendorName,
    date,
  } = transaction;

  const { tags: subcategoryTags = [] } = findSubcategoryObj(
    transactionSubcategory
  );

  const [popperAnchorEl, setPopperAnchorEl] = useState(null);
  const [activePopper, setActivePopper] = useState("");
  const [totalWidth, setTotalWidth] = useState(0);

  const inputRef = useRef();
  const inputRefCurrent = inputRef.current;

  const inputBlocks = ["offices", ...subcategoryTags];

  useEffect(() => {
    if (inputRefCurrent) {
      setTotalWidth(inputRef.current.offsetWidth - 24);
    }
  }, [inputRefCurrent]);

  const setWidth = (type) => {
    if (activePopper && activePopper !== type) {
      return 3;
    }
    if (!activePopper) {
      return 12 / inputBlocks.length;
    }
    return 12 - (inputBlocks.length - 1) * 3;
  };

  const propsObj = {
    activePopper,
    setActivePopper,
    popperAnchorEl,
    setPopperAnchorEl,
    totalWidth,
    transactionSubcategory,
  };

  const inputPositions = (idx) => {
    if (idx === 0) {
      return "left";
    }
    if (idx === inputBlocks.length - 1) {
      return "right";
    }
    return "center";
  };

  const removeEmployee = ({ id }) => {
    const filteredEmployees = employees.filter(
      (employee) => employee.id !== id
    );
    return editTransaction("employees")(filteredEmployees);
  };

  const onSelectVendor = async (vendor) => {
    const {
      revenueYears,
      id,
      name,
      naicsCode,
      naicsTitle,
      scopeThreeCategory,
      tonsCo2ePerDollar: vendorTonsCo2ePerDollar,
    } = vendor || {};

    const findTonsCo2ePerDollar = async () => {
      if (!revenueYears) {
        return vendorTonsCo2ePerDollar;
      }

      const dateYear = dayjs(date).year();

      const transactionDateRevenueYear = revenueYears.find(
        ({ year }) => year === dateYear
      );
      const mostRecentRevenueYear = revenueYears.reduce(
        (acc, revenueYear) => (revenueYear.year > acc.year ? revenueYear : acc),
        { year: 0 }
      );
      const { revenue } = transactionDateRevenueYear || mostRecentRevenueYear;

      const { yearlyTons } = await fetchOurApi({
        path: "/vendors/fetch-vendor-revenue-year-emissions",
        method: "POST",
        data: { vendorId: id, revenueYear: dateYear },
        callback: (res) => res,
      });

      return yearlyTons / revenue;
    };

    const tonsCo2ePerDollar = await findTonsCo2ePerDollar();
    const emissionData = {
      naicsCode,
      naicsTitle,
      scopeThreeCategory,
      tonsCo2ePerDollar,
    };

    editTransaction("emissionData")(emissionData);
    editTransaction("vendorId")(id);
    return editTransaction("vendorName")(name);
  };

  const inputOptions = (position) => ({
    employees: (
      <EmployeesInput
        employees={employees}
        setEmployees={editTransaction("employees")}
        removeEmployee={removeEmployee}
        position={position}
        inputLength={inputBlocks.length}
        {...propsObj}
      />
    ),
    vendors: (
      <VendorsInput
        vendor={vendorId && vendorName && { id: vendorId, name: vendorName }}
        setVendor={onSelectVendor}
        position={position}
        inputLength={inputBlocks.length}
        {...propsObj}
      />
    ),
    offices: (
      <OfficeInput
        office={office}
        setOffice={editTransaction("office")}
        position={position}
        inputLength={inputBlocks.length}
        {...propsObj}
      />
    ),
    events: (
      <EventsInput
        event={event}
        setEvent={editTransaction("event")}
        position={position}
        inputLength={inputBlocks.length}
        {...propsObj}
      />
    ),
  });

  return (
    <>
      <Grid item container spacing={1} ref={inputRef} justifyContent="center">
        {inputBlocks.map((input, idx) => (
          <Grid item sm={setWidth(input)} key={`tag-input-${idx}`}>
            {inputOptions(inputPositions(idx))[input]}
          </Grid>
        ))}
      </Grid>
    </>
  );
};
export default TransactionsTagsInputs;
